import React, { useState, useEffect, useMemo } from 'react';
import { Search, BookOpen, Clock, Tag, Star, ChevronRight, Grid, List, Filter, X, Zap, FileText } from 'lucide-react';
import '../styles/Documentation.css';
import docsData from '../data/docs.json';
import { DocumentationItem, DocCategory } from '../types';
import DocumentationViewer from '../components/docs/DocumentationViewer';

const Documentation: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentationItem | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const filteredDocs = useMemo(() => {
    return docsData.documentation.filter((doc: DocumentationItem) => {
      const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
      const matchesDifficulty = selectedDifficulty === 'all' || doc.difficulty === selectedDifficulty;
      
      return matchesSearch && matchesCategory && matchesDifficulty;
    });
  }, [searchTerm, selectedCategory, selectedDifficulty]);

  const featuredDocs = useMemo(() => {
    return docsData.documentation.filter((doc: DocumentationItem) => doc.featured);
  }, []);

  const handleDocClick = (doc: DocumentationItem) => {
    setSelectedDoc(doc);
  };

  const handleBackToList = () => {
    setSelectedDoc(null);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedDifficulty('all');
  };

  const hasActiveFilters = searchTerm || selectedCategory !== 'all' || selectedDifficulty !== 'all';

  // If a document is selected, show the viewer
  if (selectedDoc) {
    return <DocumentationViewer docItem={selectedDoc} onBack={handleBackToList} />;
  }

  return (
    <div className={`docs-page ${isLoaded ? 'docs-page--loaded' : ''}`}>
      {/* Minimal Hero Section */}
      <section className="docs-hero">
        <div className="docs-hero__content">
          <div className="docs-hero__badge">
            <BookOpen size={14} />
            <span>Documentation</span>
          </div>
          
          <h1 className="docs-hero__title">
            Everything you need to know
          </h1>
          
          <p className="docs-hero__subtitle">
            Comprehensive guides, API references, and tutorials for all projects and tools.
          </p>

          <div className="docs-hero__stats">
            <div className="docs-hero__stat">
              <span className="docs-hero__stat-number">{docsData.documentation.length}</span>
              <span className="docs-hero__stat-label">Guides</span>
            </div>
            <div className="docs-hero__stat">
              <span className="docs-hero__stat-number">{docsData.categories.length - 1}</span>
              <span className="docs-hero__stat-label">Categories</span>
            </div>
            <div className="docs-hero__stat">
              <span className="docs-hero__stat-number">{featuredDocs.length}</span>
              <span className="docs-hero__stat-label">Featured</span>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Search Section */}
      <section className="docs-search">
        <div className="docs-search__container">
          <div className={`docs-search__bar ${searchFocused ? 'docs-search__bar--focused' : ''}`}>
            <div className="docs-search__input-wrapper">
              <Search className="docs-search__icon" />
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="docs-search__input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="docs-search__clear"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="docs-search__controls">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`docs-search__filter-toggle ${showFilters ? 'docs-search__filter-toggle--active' : ''}`}
            >
              <Filter size={16} />
              <span>Filters</span>
              {hasActiveFilters && <div className="docs-search__filter-dot" />}
            </button>

            <div className="docs-search__view-controls">
              <button
                className={`docs-search__view-btn ${viewMode === 'grid' ? 'docs-search__view-btn--active' : ''}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <Grid size={16} />
              </button>
              <button
                className={`docs-search__view-btn ${viewMode === 'list' ? 'docs-search__view-btn--active' : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <List size={16} />
              </button>
            </div>
          </div>

          {/* Collapsible Filters */}
          <div className={`docs-search__filters ${showFilters ? 'docs-search__filters--open' : ''}`}>
            <div className="docs-search__filters-content">
              <div className="docs-search__filter-group">
                <label className="docs-search__filter-label">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="docs-search__filter-select"
                >
                  {docsData.categories.map((category: DocCategory) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="docs-search__filter-group">
                <label className="docs-search__filter-label">Difficulty</label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="docs-search__filter-select"
                >
                  <option value="all">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="docs-search__clear-filters"
                >
                  <X size={14} />
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Search Results Count */}
          {(searchTerm || hasActiveFilters) && (
            <div className="docs-search__results">
              <span className="docs-search__results-count">
                {filteredDocs.length} result{filteredDocs.length !== 1 ? 's' : ''}
              </span>
              {searchTerm && (
                <span className="docs-search__results-query">
                  for "{searchTerm}"
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Featured Documentation */}
      {!searchTerm && !hasActiveFilters && featuredDocs.length > 0 && (
        <section className="docs-featured">
          <div className="docs-featured__header">
            <h2 className="docs-featured__title">
              <Star size={18} />
              Featured Documentation
            </h2>
          </div>
          <div className="docs-featured__grid">
            {featuredDocs.map((doc: DocumentationItem) => (
              <div
                key={doc.id}
                className="docs-featured__card"
                onClick={() => handleDocClick(doc)}
              >
                <div className="docs-featured__card-header">
                  <div className="docs-featured__card-emoji">{doc.emoji}</div>
                  <div className="docs-featured__card-meta">
                    <span className="docs-featured__card-category">
                      {docsData.categories.find(cat => cat.id === doc.category)?.name}
                    </span>
                    {doc.difficulty && (
                      <span className={`docs-featured__card-difficulty docs-featured__card-difficulty--${doc.difficulty}`}>
                        <Zap size={10} />
                        {doc.difficulty}
                      </span>
                    )}
                  </div>
                </div>
                <div className="docs-featured__card-content">
                  <h3 className="docs-featured__card-title">{doc.title}</h3>
                  <p className="docs-featured__card-description">{doc.description}</p>
                </div>
                <div className="docs-featured__card-footer">
                  <div className="docs-featured__card-info">
                    <Clock size={12} />
                    <span>{doc.estimatedReadTime}</span>
                  </div>
                  <ChevronRight className="docs-featured__card-arrow" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Documentation Grid */}
      <section className="docs-content">
        <div className="docs-content__header">
          <h2 className="docs-content__title">
            {searchTerm ? 'Search Results' : hasActiveFilters ? 'Filtered Results' : 'All Documentation'}
          </h2>
          <span className="docs-content__count">
            {filteredDocs.length} item{filteredDocs.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className={`docs-grid docs-grid--${viewMode}`}>
          {filteredDocs.map((doc: DocumentationItem) => (
            <div
              key={doc.id}
              className="docs-card"
              onClick={() => handleDocClick(doc)}
            >
              <div className="docs-card__header">
                <div className="docs-card__emoji">{doc.emoji}</div>
                <div className="docs-card__meta">
                  <span className="docs-card__category">
                    {docsData.categories.find(cat => cat.id === doc.category)?.name}
                  </span>
                  {doc.featured && (
                    <div className="docs-card__featured">
                      <Star size={10} />
                    </div>
                  )}
                </div>
              </div>

              <div className="docs-card__content">
                <h3 className="docs-card__title">{doc.title}</h3>
                <p className="docs-card__description">{doc.description}</p>

                <div className="docs-card__tags">
                  {doc.tags.slice(0, 3).map((tag: string) => (
                    <span key={tag} className="docs-card__tag">
                      <Tag size={8} />
                      {tag}
                    </span>
                  ))}
                  {doc.tags.length > 3 && (
                    <span className="docs-card__tag docs-card__tag--more">
                      +{doc.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>

              <div className="docs-card__footer">
                <div className="docs-card__info">
                  <Clock size={12} />
                  <span>{doc.estimatedReadTime}</span>
                  {doc.difficulty && (
                    <span className={`docs-card__difficulty docs-card__difficulty--${doc.difficulty}`}>
                      <Zap size={10} />
                      {doc.difficulty}
                    </span>
                  )}
                </div>
                <ChevronRight className="docs-card__arrow" />
              </div>
            </div>
          ))}
        </div>

        {filteredDocs.length === 0 && (
          <div className="docs-empty">
            <div className="docs-empty__icon">
              <FileText size={48} />
            </div>
            <h3 className="docs-empty__title">No documentation found</h3>
            <p className="docs-empty__description">
              {searchTerm
                ? `No results found for "${searchTerm}". Try different keywords or clear your search.`
                : 'No documentation matches your current filters.'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="docs-empty__button"
              >
                <X size={16} />
                Clear all filters
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Documentation; 